/**
 * MIRA Dialog Component
 * Wraps Shadcn Dialog with MIRA design system styles
 */

import React from 'react';
import {
  Dialog as ShadcnDialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import styles from '@/styles/components/layout/dialog.module.css';

/**
 * A content element, in either of the two forms callers can pass.
 *
 * `@/components/mira` publishes `DialogContentMira` under the name
 * `DialogContent`, so what a caller passes is never the Shadcn component this
 * file imports. Testing only against that one meant no caller was ever
 * recognised, and every dialog that supplied its own content got wrapped in a
 * second one — a second portal, a second overlay and a second scroll lock. The
 * two contents are siblings under `body`, so the active lock's shard did not
 * contain the visible dialog and `react-remove-scroll` cancelled every wheel
 * and touchmove over it. Dragging the scrollbar fires neither, which is why
 * that was the only way to scroll a long dialog.
 */
const isDialogContent = (child) =>
  React.isValidElement(child) &&
  (child.type === DialogContent || child.type === DialogContentMira);

/**
 * Does this subtree already render one of `types`, at any depth?
 *
 * The element that matters is almost never a direct child — the convention is
 * `<DialogContent><DialogHeader><DialogTitle>` — so a one-level check found
 * nothing and the fallbacks below were always injected. That was invisible only
 * while they were landing in a throwaway second dialog. Now that they land in
 * the caller's own content, a miss would put a hidden "Dialog" heading ahead of
 * the real title and Radix would label the dialog with it.
 *
 * Stops at custom components, whose internals are not inspectable from here.
 */
const subtreeHas = (children, types) =>
  React.Children.toArray(children).some(
    (child) =>
      React.isValidElement(child) &&
      (types.includes(child.type) || subtreeHas(child.props?.children, types))
  );

export const Dialog = ({
  children,
  show,
  open,
  width,
  className,
  onOpenChange,
  showClose = true,
  ...props
}) => {
  const resolvedOpen = open ?? show;
  const dialogProps = { ...props };

  if (resolvedOpen !== undefined) {
    dialogProps.open = resolvedOpen;
  }

  // Remove legacy props so they don't reach Radix
  if (dialogProps.show !== undefined) {
    delete dialogProps.show;
  }
  if (dialogProps.width !== undefined) {
    delete dialogProps.width;
  }

  const childArray = React.Children.toArray(children);
  const hasDialogContent = childArray.some(isDialogContent);

  // Check if any descendant already provides DialogTitle / DialogDescription
  // to avoid duplicate accessibility elements
  const hasDialogTitle = subtreeHas(children, [DialogTitle, DialogTitleMira]);
  const hasDialogDescription = subtreeHas(children, [
    DialogDescription,
    DialogDescriptionMira,
  ]);

  // Accessibility fallbacks: visually-hidden title + suppress describedby warning
  const a11yFallback = (
    <>
      {!hasDialogTitle && (
        <DialogTitle className="sr-only">Dialog</DialogTitle>
      )}
      {!hasDialogDescription && (
        <DialogDescription className="sr-only">Dialog content</DialogDescription>
      )}
    </>
  );

  const mergedContent = hasDialogContent
    ? childArray.map((child) => {
        if (isDialogContent(child)) {
          return React.cloneElement(child, {
            className: cn(child.props.className, width, className),
            showClose:
              child.props.showClose !== undefined
                ? child.props.showClose
                : showClose,
          }, a11yFallback, child.props.children);
        }
        return child;
      })
    : [
        <DialogContent
          key="mira-dialog-content"
          className={cn(width, className)}
          showClose={showClose}
        >
          {a11yFallback}
          {children}
        </DialogContent>,
      ];

  return (
    <ShadcnDialog {...dialogProps} onOpenChange={onOpenChange}>
      {mergedContent}
    </ShadcnDialog>
  );
};

export const DialogContentMira = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return (
      <DialogContent
        ref={ref}
        className={cn(styles.miraDialogContent, className)}
        {...props}
      >
        {children}
      </DialogContent>
    );
  }
);

DialogContentMira.displayName = 'MiraDialogContent';

export const DialogHeaderMira = ({ className, ...props }) => {
  return (
    <DialogHeader className={cn(styles.miraDialogHeader, className)} {...props} />
  );
};

export const DialogTitleMira = React.forwardRef(
  ({ className, ...props }, ref) => {
    return (
      <DialogTitle
        ref={ref}
        className={cn(styles.miraDialogTitle, className)}
        {...props}
      />
    );
  }
);

DialogTitleMira.displayName = 'MiraDialogTitle';

export const DialogDescriptionMira = React.forwardRef(
  ({ className, ...props }, ref) => {
    return (
      <DialogDescription
        ref={ref}
        className={cn(styles.miraDialogDescription, className)}
        {...props}
      />
    );
  }
);

DialogDescriptionMira.displayName = 'MiraDialogDescription';

export const DialogFooterMira = ({ className, ...props }) => {
  return (
    <DialogFooter className={cn(styles.miraDialogFooter, className)} {...props} />
  );
};

// Re-export other Dialog components
export { DialogClose, DialogTrigger } from '@/components/ui/dialog';
